import client from './client';

export interface TripDocument {
  id: number;
  tripId: number | null;
  expenseId: number | null;
  paymentId: number | null;
  documentType: string;
  filePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: number;
  uploadedAt: string;
  notes: string | null;
}

export interface PaymentReceiptData {
  id: number;
  receiptNumber: string;
  date: string;
  amount: string;
  method: string;
  trip: {
    pickup: string;
    delivery: string;
    date: string;
    vehicle: string;
  };
  notes: string;
}

export interface ExpenseReceiptData {
  id: number;
  receiptNumber: string;
  date: string;
  amount: string;
  expenseType: string;
  vehicle: string;
  tripId: number | null;
  description: string;
  quantity: number | null;
  unitPrice: string | null;
}

export const documentAPI = {
  // Get documents for a trip
  getByTrip: async (tripId: number): Promise<TripDocument[]> => {
    const response = await client.get(`/trips/${tripId}/documents`);
    return response.data.data;
  },

  // Get documents for an expense
  getByExpense: async (expenseId: number): Promise<TripDocument[]> => {
    const response = await client.get(`/expenses/${expenseId}/documents`);
    return response.data.data;
  },

  // Get documents for a payment
  getByPayment: async (paymentId: number): Promise<TripDocument[]> => {
    const response = await client.get(`/payments/${paymentId}/documents`);
    return response.data.data;
  },

  // Get document info
  getById: async (id: number): Promise<TripDocument> => {
    const response = await client.get(`/documents/${id}/info`);
    return response.data.data;
  },

  // Download document (returns blob)
  download: async (id: number): Promise<Blob> => {
    const response = await client.get(`/documents/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Preview document (returns blob for inline viewing)
  preview: async (id: number): Promise<Blob> => {
    const response = await client.get(`/documents/${id}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Delete document
  delete: async (id: number): Promise<void> => {
    await client.delete(`/documents/${id}`);
  },

  // Upload document to trip
  uploadToTrip: async (
    tripId: number,
    file: File,
    documentType: string = 'TRIP_DOCUMENT',
    notes?: string
  ): Promise<TripDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (notes) formData.append('notes', notes);

    const response = await client.post(`/trips/${tripId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  // Upload document to expense
  uploadToExpense: async (
    expenseId: number,
    file: File,
    documentType: string = 'EXPENSE_INVOICE',
    notes?: string
  ): Promise<TripDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (notes) formData.append('notes', notes);

    const response = await client.post(`/expenses/${expenseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  // Upload document to payment
  uploadToPayment: async (
    paymentId: number,
    file: File,
    documentType: string = 'PAYMENT_PROOF',
    notes?: string
  ): Promise<TripDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (notes) formData.append('notes', notes);

    const response = await client.post(`/payments/${paymentId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  // Get payment receipt data (JSON) for browser-based printing
  getPaymentReceiptData: async (paymentId: number): Promise<PaymentReceiptData> => {
    const response = await client.get(`/payments/${paymentId}/receipt-data`);
    return response.data.data;
  },

  // Get expense receipt data (JSON) for browser-based printing
  getExpenseReceiptData: async (expenseId: number): Promise<ExpenseReceiptData> => {
    const response = await client.get(`/expenses/${expenseId}/receipt-data`);
    return response.data.data;
  },
};
